import { Injectable } from '@angular/core';
import {forkJoin, Observable, of, switchMap} from 'rxjs';
import { map } from 'rxjs/operators';
import { BullhornEntityType } from '../types/bullhorn/bullhorn-entity-type';
import { BullhornDataService } from './bullhorn-data.service';

@Injectable({
  providedIn: 'root',
})
export class BullhornMetaService {
  META_KEY = 'bullhornMeta';

  constructor(private bullhornDataService: BullhornDataService,
  ) { }

  public toBullhornEntity(entity: string) {
    switch (entity) {
      case 'Job Posting':
        return BullhornEntityType.JOB_ORDER;
      case 'Client Contact':
        return BullhornEntityType.CLIENT_CONTACT;
      case 'Client':
        return BullhornEntityType.CLIENT_CORPORATION;
      default:
        return entity as BullhornEntityType;
    }
  }

  public getAllEntityMeta() {
    if (!this.getMeta()) {
      return this.bullhornDataService.getLeadsAndOpportuniteisEnabled().pipe(switchMap((enabled) => {
          const forkJoinSources = [
            this.bullhornDataService.getEntityMeta(BullhornEntityType.JOB_ORDER, ['id'], 'basic'),
            this.bullhornDataService.getEntityMeta(BullhornEntityType.JOB_SUBMISSION, ['id'], 'basic'),
            this.bullhornDataService.getEntityMeta(BullhornEntityType.CANDIDATE, ['id'], 'basic'),
            this.bullhornDataService.getEntityMeta(BullhornEntityType.CLIENT_CONTACT, ['id'], 'basic'),
            this.bullhornDataService.getEntityMeta(BullhornEntityType.PLACEMENT, ['id'], 'basic'),
            this.bullhornDataService.getEntityMeta(BullhornEntityType.CLIENT_CORPORATION, ['id'], 'basic'),
          ];
          if (enabled) {
            forkJoinSources.push(this.bullhornDataService.getEntityMeta(BullhornEntityType.OPPORTUNITY, ['id'], 'basic'));
            forkJoinSources.push(this.bullhornDataService.getEntityMeta(BullhornEntityType.LEAD, ['id'], 'basic'));
          }
          return this.getBhMeta(forkJoinSources);
        })
      );
    } else {
      return of(this.getLabelPairs());
    }
  }

  private getBhMeta(forkJoinSources: Observable<IBullhornEntityMeta>[]): Observable<IEntityLabelPair[]> {
    return forkJoin(forkJoinSources).pipe(map((result) => {
      const bullhornMeta: IBullhornMeta = {entities: []};
      result.forEach((entityMeta) => {
        bullhornMeta.entities.push({
          objectName: entityMeta.entity,
          label: entityMeta.label
        });
      });
      this.setMeta(bullhornMeta);
      console.log('Got Bullhorn meta for corp', bullhornMeta);
      return bullhornMeta.entities as IEntityLabelPair[];
    }));
  }

  private setMeta(meta: IBullhornMeta) {
    localStorage.setItem(this.META_KEY, `${JSON.stringify(meta)}`);
  }

  private getMeta(): IBullhornMeta {
    return JSON.parse(localStorage.getItem(this.META_KEY) as string);
  }

  public getLabelPairs(): IEntityLabelPair[] {
    const meta = JSON.parse(localStorage.getItem(this.META_KEY) as string);
    return meta.entities;
  }
}

export interface IBullhornMeta {
  entities: IEntityLabelPair[];
}

export interface IBullhornEntityMeta {
  entity: BullhornEntityType;
  entityMetaUrl: string;
  label: string;
  dateLastModified: string;
  fields: IBullhornFieldMeta[];
}

export interface IBullhornFieldMeta {
  name: string;
  label: string;
  maxLength?: number;
  dataType?: string;
}

export interface IEntityLabelPair {
  objectName: string;
  label: string;
}
